import React from "react";
/**
 * CaughtSprite — colour-art renderer for CAUGHT dex cells (Build 7).
 *
 * Completion payoff: once a species is caught the dex cell should reward the
 * trainer with full colour art instead of the shared Style B silhouette.
 * Uncaught cells keep rendering <PokemonSilhouette/> at the call site — this
 * component is only mounted for caught cells.
 *
 * Source priority:
 *   1. form art, for a Mega / G-Max / specific-form cell
 *   2. in-app bundled colour art (offline from first launch)
 *   3. official artwork from our own mirror
 *   4. HOME art (onError fallback)
 *   5. PokemonSilhouette (final fallback if every image fails to load, so a
 *      caught cell is never blank and never shifts layout)
 *
 * THE TRAINER'S OWN SCAN CROP USED TO COME FIRST, and is gone (2026-08-13).
 * Showing a trainer their real in-game art sounds better than it looked: the
 * crop is a low-resolution lift off a phone screenshot, so a caught cell got a
 * murky little sprite where its neighbour had a clean render, and which one you
 * got depended on nothing a reader could see — whether that species happened to
 * be scanned recently. The official mirror is the same art everywhere.
 *
 * It was also expensive. Every crop had to be captured mid-scan: a full-frame
 * decode, a PNG encode and a main-thread localStorage write PER CELL, inside
 * the window a frame has to finish in. That capture is now gone from the
 * scanner (see dexPipelineRunner), so there is nothing new to read here anyway.
 *
 * Same footprint as the silhouette it replaces — no layout shift between
 * caught and uncaught states.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isSpriteWarm } from "@/lib/spriteWarm";
import { PokemonSilhouette } from "./PokemonSilhouette";
import { bundledArtworkUrl, officialArtworkUrl, homeArtworkUrl } from "@/lib/pokemonArtwork";
import { parseFormSpriteId } from "@/lib/formSpriteCandidates";

interface CaughtSpriteProps {
  id: number;
  size?: number;
  shiny?: boolean;
  // When true, wrap the rendered sprite in a blue hue-rotate filter. Used by
  // the Shiny Perfect dex to give caught cells a distinct blue tint.
  blueTint?: boolean;
  // When true, wrap the rendered sprite in a gold hue-rotate filter. Used by
  // the Shiny Lucky dex to give caught cells a distinct gold tint.
  goldTint?: boolean;
  ariaLabel?: string;
  className?: string;
  /**
   * E43: when true the sprite fills its parent box (width/height 100%,
   * object-fit contain) instead of a fixed `size` px square. The dex cell
   * uses this so the colour art is bounded by the cell's sprite region and
   * never overflows the rounded border.
   */
  fill?: boolean;
  /**
   * Ordered list of form-specific colour-art candidate URLs (e.g.
   * pokesprite gen8 then gen7x for a Mega/G-Max cell). When present and
   * non-empty these are tried FIRST, before falling back to the
   * base-species artwork. This
   * is how the Mega / G-Max dex grids render true form art instead of the
   * base species; an empty/undefined list keeps the original base-id
   * behaviour for every other dex.
   */
  formSpriteCandidates?: string[];
  /**
   * Atlas form slug (e.g. "6-mega-x", "25-gmax") used ONLY for the final
   * silhouette fallback so a Mega/G-Max cell whose colour art fails to load
   * still shows the correct form shape rather than the base species.
   */
  formSlug?: string;
}

type ArtStage = "form" | "bundled" | "official" | "home" | "silhouette";

/**
 * How long an ON-DEVICE source (the precache `local` file or the in-app
 * `bundled` webp) may sit without firing load OR error before we treat it as
 * stalled and walk to the next source.
 *
 * Why this exists: Capacitor's asset / `_capacitor_file_` bridge can leave a
 * request PENDING FOREVER under a burst of concurrent loads — e.g. the ~40-cell
 * profile dex grid re-mounting on navigation fires every sprite request at once
 * and some never resolve. With no `error` event the fallback chain never
 * advances, so the cell stayed blank until it unmounted (leaving the page and
 * coming back "fixed" it). On-device art should paint in a few ms, so a couple
 * of seconds of silence is a stall, not slowness. Network stages (official /
 * home) are exempt — they are legitimately slow and error on real failure.
 */
const STALLED_ONDEVICE_MS = 3000;

function CaughtSpriteInner({
  id,
  size = 72,
  shiny = false,
  blueTint = false,
  goldTint = false,
  ariaLabel,
  className = "",
  fill = false,
  formSpriteCandidates,
  formSlug,
}: CaughtSpriteProps) {
  // Normalise the form-candidate list once; an empty list means "no form
  // art for this cell" and the chain behaves exactly as before.
  const formChain = useMemo<{ list: string[]; bundledIdx: number }>(() => {
    const given = Array.isArray(formSpriteCandidates) ? formSpriteCandidates.filter(Boolean) : [];
    if (given.length === 0) return { list: given, bundledIdx: -1 };
    // Alt forms, Megas and G-Max are bundled into the binary too
    // (bundle-color-sprites.ts covers the ids above 1025), so put the local tile
    // at the FRONT of the form chain — the same first-launch-offline benefit
    // base cells already had.
    //
    // The id is taken from the FIRST candidate that carries one. A form keyed
    // by a PokéAPI id (a regional, a Mega, a G-Max) leads its chain with that
    // id's HOME render; the entries that parse to nothing are the name-keyed
    // pokesprite fallbacks at the TAIL of a Mega/G-Max chain.
    //
    // EXCEPT a costume (2026-09-28). A costume chain leads with a name-keyed
    // game icon (pokeminers/pm25.f…) and ENDS on the base species' HOME +
    // pixel art as the last resort (formSpriteCandidates' fallbackSpeciesId).
    // The scan walked past the icon to that tail, read the BASE species' id
    // off it, and put the base species' bundled tile in FRONT of the costume:
    // a costumed Pikachu in My Pokémon painted as plain Pikachu the instant it
    // mounted and only reached its costume when the bundled load stalled and
    // the 3 s guard walked the chain on (owner: "costume sprites take a while
    // to resolve"). A name-keyed lead whose only id is the base species' own
    // is that case: the costume's art is the first thing tried, and the base
    // bundled tile waits at the `bundled` stage behind the whole chain.
    let formId: number | null = null;
    for (const candidate of given) {
      formId = parseFormSpriteId(candidate);
      if (formId != null) break;
    }
    if (formId == null) return { list: given, bundledIdx: -1 };
    if (parseFormSpriteId(given[0]) == null && formId === id) return { list: given, bundledIdx: -1 };
    // Not gated on existence: a tile the bundle lacks 404s and the <img> onError
    // walks straight on to the CDN candidates below it, the same mechanism base
    // cells rely on. bundledIdx is tracked so the stall guard below knows which
    // entry of this chain is on-device.
    return { list: [bundledArtworkUrl(formId, { shiny }), ...given], bundledIdx: 0 };
  }, [formSpriteCandidates, shiny]);
  const formCandidates = formChain.list;
  const formBundledIdx = formChain.bundledIdx;
  const hasFormArt = formCandidates.length > 0;

  // In-app bundled colour art, shipped in the binary and available OFFLINE
  // from first launch. This is the PRIMARY on-device source for a base cell
  // now that the redundant full-res precache/download has been removed — a
  // stray not in the bundle 404s and falls through to the CDN `official` art.
  // Existence is decided by the <img> onError, not here.
  //
  // For a FORM cell it is the stage AFTER the form chain (2026-09-28): a
  // costume whose icon is unreachable (offline, or one of the icons the
  // source does not have yet) lands on the base species' bundled tile rather
  // than walking out to the CDN for the same base art.
  const bundledSrc = bundledArtworkUrl(id, { shiny });

  // Where the chain starts: form art if this is a form cell, else the in-app
  // bundled art, else the CDN official artwork.
  const firstStage: ArtStage = hasFormArt ? "form" : "bundled";

  // Index into formCandidates while we are in the "form" stage; advanced by
  // onError until the list is exhausted, then we fall through to official.
  const [formIdx, setFormIdx] = useState(0);

  // Start from the best available source; the <img> onError handler walks
  // down the fallback chain when a source fails to load.
  const [stage, setStage] = useState<ArtStage>(firstStage);

  // If form-art availability changes after mount, re-seat the stage so we do
  // not get stuck on a stale fallback.
  useEffect(() => {
    setFormIdx(0);
    setStage(firstStage);
  }, [firstStage]);

  // Walk to the next source. Called on <img> error, on a "loaded but empty"
  // (0x0) image, and by the stall timer below. Extracted so all three failure
  // signals share one chain instead of drifting apart.
  const advanceOnFailure = useCallback(() => {
    if (stage === "form") {
      // Try the next form candidate; when exhausted, fall to the in-app bundled
      // art, then the CDN.
      const next = formIdx + 1;
      if (next < formCandidates.length) setFormIdx(next);
      else setStage(bundledSrc ? "bundled" : "official");
      return;
    }
    setStage((s) =>
      s === "bundled" ? "official" : s === "official" ? "home" : "silhouette",
    );
  }, [stage, formIdx, formCandidates.length, bundledSrc]);

  const src =
    stage === "form"
      ? (formCandidates[formIdx] ?? null)
      : stage === "bundled"
        ? bundledSrc
        : stage === "official"
          ? officialArtworkUrl(id, { shiny })
          : stage === "home"
            ? homeArtworkUrl(id, { shiny })
            : null;

  // Stall guard for the ON-DEVICE `bundled` stage. The bundled webp is served by
  // Capacitor's asset bridge, which can leave a request pending under a burst of
  // concurrent loads (the profile grid re-mounting) — no load, no error, no
  // fallback → a blank cell that only a remount cleared. If it hasn't painted
  // within STALLED_ONDEVICE_MS, treat it as failed and advance to the CDN.
  // Network stages are not guarded (they are legitimately slow and error).
  //
  // The FORM stage is now mixed: its first entry is the bundled form tile (also
  // on-device, also through the asset bridge) and the rest are CDN URLs. So the
  // guard covers that one entry too — without it a stalled form tile would blank
  // the cell with no recovery, which is precisely the failure this exists for —
  // while the network candidates after it stay unguarded.
  const onDeviceStage =
    stage === "bundled" || (stage === "form" && formIdx === formBundledIdx);
  useEffect(() => {
    if (!onDeviceStage) return;
    if (!src) return;
    const t = window.setTimeout(advanceOnFailure, STALLED_ONDEVICE_MS);
    return () => window.clearTimeout(t);
  }, [onDeviceStage, src, advanceOnFailure]);

  // THE LAST PAINTED FRAME STAYS UP (2026-10-07 owner report: sprites
  // "flickering in and out"). Every stage change swaps `src`, and the browser
  // shows nothing between the old image and the new one's first paint. So the
  // last source that loaded is remembered, and while the live <img> is still
  // loading a different one it sits transparent over a copy of that frame.
  // The live <img> stays the element the chain drives (error / load / stall).
  const [paintedSrc, setPaintedSrc] = useState<string | null>(null);
  const ghostSrc = paintedSrc && src && paintedSrc !== src ? paintedSrc : null;

  const tintFilter = blueTint
    ? "hue-rotate(180deg) saturate(1.2)"
    : goldTint
      ? "hue-rotate(45deg) saturate(1.3) brightness(1.05)"
      : null;

  const withTint = (node: JSX.Element) =>
    tintFilter ? (
      <span
        style={{
          filter: tintFilter,
          display: "block",
          width: fill ? "100%" : undefined,
          height: fill ? "100%" : undefined,
        }}
      >
        {node}
      </span>
    ) : (
      node
    );

  if (stage === "silhouette" || !src) {
    return withTint(
      <PokemonSilhouette
        id={id}
        size={size}
        fill={fill}
        caught
        shiny={shiny}
        formSlug={formSlug}
        ariaLabel={ariaLabel}
        className={className}
      />,
    );
  }

  const liveImg = (
    <img
      src={src}
      alt={ariaLabel ?? `Species ${id}`}
      width={fill ? undefined : size}
      height={fill ? undefined : size}
      decoding="async"
      // A URL the boot warm has already loaded paints from the memory cache,
      // so it is fetched eagerly: no pop-in when a culled row scrolls into
      // view. Anything else stays lazy, as before.
      loading={isSpriteWarm(src) ? "eager" : "lazy"}
      // Native shell: never draggable, never offers the iOS "Save Image /
      // Share" callout (the CSS img rule in index.css enforces this too).
      draggable={false}
      data-testid="caught-sprite"
      data-art-stage={stage}
      data-fill={fill ? "true" : "false"}
      className={`pokemon-caught-art ${className}`}
      style={
        fill
          ? {
              width: "100%",
              height: "100%",
              objectFit: "contain",
              objectPosition: "center",
              display: "block",
              ...(ghostSrc ? { position: "absolute", inset: 0, opacity: 0 } : null),
            }
          : {
              width: size,
              height: size,
              objectFit: "contain",
              display: "block",
              ...(ghostSrc ? { position: "absolute", inset: 0, opacity: 0 } : null),
            }
      }
      onError={advanceOnFailure}
      onLoad={(e) => {
        // A "successful" load with no pixels (naturalWidth 0) is a silent
        // failure: some Capacitor-bridge responses fire `load` on a 0-byte /
        // non-image body, so `error` never comes and the cell would sit blank.
        // Treat it as a failure and walk to the next source.
        if (e.currentTarget.naturalWidth === 0) {
          advanceOnFailure();
          return;
        }
        setPaintedSrc(e.currentTarget.getAttribute("src"));
      }}
    />
  );

  if (!ghostSrc) return withTint(liveImg);
  return withTint(
    <span
      style={{
        position: "relative",
        display: "block",
        width: fill ? "100%" : size,
        height: fill ? "100%" : size,
      }}
      data-testid="caught-sprite-frame"
    >
      <img
        src={ghostSrc}
        alt=""
        aria-hidden
        draggable={false}
        data-testid="caught-sprite-previous"
        className={`pokemon-caught-art ${className}`}
        style={{ width: "100%", height: "100%", objectFit: "contain", objectPosition: "center", display: "block" }}
      />
      {liveImg}
    </span>,
  );
}

// Memoised (2026-10-06): a dex switch re-renders every grid cell, and the
// sprite under a cell whose art did not change (every missing cell, and
// every caught cell between two dexes that share art) used to re-run its
// hooks and atlas lookups anyway. Props are plain values and a stable array.
export const CaughtSprite = React.memo(CaughtSpriteInner);
